import { useEffect, useRef } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import {
  useGetCommentInitialPageStart,
  type Comment
} from 'lib/comments-common';

const SCROLL_DELAY_MS = 150;
const URL_CLEANUP_DELAY_MS = 500;

interface UseLoadMoreScrollToCommentOptions {
  commentId: string | null | undefined;
  comments: Comment[];
  pageSize?: number;
}

export function useLoadMoreScrollToComment({
  commentId,
  comments,
  pageSize = 10
}: UseLoadMoreScrollToCommentOptions) {
  const location = useLocation();
  const params = useParams();
  const {
    found,
    pageStart,
    commentId: targetCommentId
  } = useGetCommentInitialPageStart({
    commentId,
    comments,
    pageSize
  });

  const listContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!found || !targetCommentId) return;
    let cleanupTimer: ReturnType<typeof setTimeout> | undefined;
    const timer = setTimeout(() => {
      const container = listContainerRef.current;
      const el = container
        ? container.querySelector(`[data-comment-id="${targetCommentId}"]`)
        : document.querySelector(`[data-comment-id="${targetCommentId}"]`);
      if (el) {
        el.scrollIntoView({ block: 'center', inline: 'nearest' });
        // Remove commentId from URL after successful scroll using History API
        // so React Router state stays unchanged and the comment panel does not refresh
        const issueId = params.issueId;
        if (issueId) {
          const cleanPath = `/moments/${encodeURIComponent(issueId)}`;
          const cleanUrl = location.search
            ? `${cleanPath}${location.search}`
            : cleanPath;
          cleanupTimer = setTimeout(() => {
            window.history.replaceState(null, '', cleanUrl);
          }, URL_CLEANUP_DELAY_MS);
        }
      }
    }, SCROLL_DELAY_MS);
    return () => {
      clearTimeout(timer);
      if (cleanupTimer) clearTimeout(cleanupTimer);
    };
  }, [found, targetCommentId, params.issueId, location.search]);

  return { listContainerRef, found, pageStart };
}
